/** Leitura de ficheiros escolhidos pelo utilizador, com a falha mais
 *  comum traduzida.
 *
 *  Quando alguém escolhe um ficheiro, o browser guarda uma referência,
 *  não os bytes. Se os bytes não estiverem lá no momento em que os
 *  pedimos, o File API atira um `NotReadableError` cuja mensagem vem em
 *  inglês e fala de permissões, o que quase nunca é o motivo. Os motivos
 *  reais:
 *
 *  - **é grande de mais para o browser o ler de uma vez.** O JavaScript
 *    não tem cadeias de texto acima de meio gigabyte a um, nem blocos de
 *    memória acima de uns poucos. O Chrome recusa com o MESMO erro que
 *    usa para um ficheiro inacessível. Este é o único motivo que se
 *    consegue provar daqui, porque o tamanho sabe-se, e por isso é o
 *    primeiro a ser verificado. Faltava, e deu uma mensagem errada: um
 *    PTS de 16 GB nas Transferências foi diagnosticado como estando no
 *    OneDrive;
 *  - o ficheiro está numa pasta sincronizada (OneDrive, Google Drive) e
 *    é só um marcador que nunca foi descarregado para o disco;
 *  - foi movido, renomeado ou alterado depois de ter sido escolhido;
 *  - está aberto e trancado por outro programa, ou o antivírus prende-o.
 *
 *  Em qualquer dos casos a pessoa precisa de saber que o problema não
 *  está no conteúdo do ficheiro nem no nosso leitor: nós nunca chegámos
 *  a ver um único byte.
 */

function ficheiroIlegivel(erro: unknown): boolean {
  if (!(erro instanceof DOMException)) return false
  return erro.name === 'NotReadableError' || erro.name === 'NotFoundError'
}

/** Acima disto, a falha é quase de certeza o tamanho e não o disco.
 *  Meio gigabyte é o limite prático de uma cadeia de texto no V8; para
 *  bytes o limite é mais alto, mas a partir daqui a leitura de uma vez
 *  já é um risco que não vale a pena correr. */
export const LIMITE_DE_UMA_VEZ = 512 * 1024 * 1024

function gigas(bytes: number): string {
  return (bytes / 1024 ** 3).toFixed(1).replace('.', ',')
}

function mensagemDeTamanho(nome: string, tamanho: number): string {
  return (
    `"${nome}" tem ${gigas(tamanho)} GB, e é grande de mais para o browser ` +
    'o ler de uma vez. O ficheiro está bem e o disco também: o limite é ' +
    'do browser. Os formatos que lemos aos pedaços (PTS e XYZ) abrem ' +
    'qualquer tamanho; para os outros, reduz a nuvem antes de a abrir.'
  )
}

function mensagem(nome: string, tamanho = 0): string {
  if (tamanho > LIMITE_DE_UMA_VEZ) return mensagemDeTamanho(nome, tamanho)
  return (
    `Não foi possível ler "${nome}". O ficheiro foi escolhido, mas o ` +
    'browser já não consegue chegar aos bytes. Costuma ser um ficheiro ' +
    'numa pasta sincronizada (OneDrive, Google Drive) que ainda não foi ' +
    'descarregado para o disco, ou um ficheiro movido, renomeado ou ' +
    'aberto noutro programa. Abre-o uma vez para o descarregar por ' +
    'inteiro, ou copia-o para uma pasta local, e tenta de novo.'
  )
}

/** Os bytes do ficheiro, ou um erro que explica porque não os há. */
export async function readFileBytes(file: File): Promise<ArrayBuffer> {
  try {
    return await file.arrayBuffer()
  } catch (erro) {
    if (ficheiroIlegivel(erro))
      throw new Error(mensagem(file.name, file.size), { cause: erro })
    throw erro
  }
}

/** O texto do ficheiro, com o mesmo cuidado. */
export async function readFileText(file: File): Promise<string> {
  try {
    return await file.text()
  } catch (erro) {
    if (ficheiroIlegivel(erro))
      throw new Error(mensagem(file.name, file.size), { cause: erro })
    throw erro
  }
}
